import React, { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Building, DollarSign, CheckSquare, Upload, FileText, CheckCircle, AlertCircle, X, ChevronRight, ChevronLeft, MapPin, Calendar, Image, Trash2, Info, Plus } from 'lucide-react';
import { Sidebar } from '../../components/shared/Sidebar';
import { DashboardHeader } from '../../components/shared/DashboardHeader';
import { useToast } from '../../hooks/useToast';
import { formatCurrency } from '../../utils/calculations';
import { Property } from '../../types';
type ListingStep = 'details' | 'pricing' | 'amenities' | 'photos' | 'preview';
interface PropertyFormData {
  address: string;
  city: string;
  state: string;
  propertyType: Property['propertyType'];
  bedrooms: number;
  bathrooms: number;
  description: string;
  annualRent: number;
  securityDeposit: number;
  leaseDuration: 6 | 12 | 24;
  availableFrom: string;
  amenities: string[];
  additionalFeatures: string;
  photos: {
    id: string;
    name: string;
    url: string;
  }[];
  ownershipDocs: {
    id: string;
    type: string;
    name: string;
    file?: File;
  }[];
}
interface OwnershipDoc {
  id: string;
  type: 'title_deed' | 'certificate_of_occupancy' | 'purchase_agreement';
  name: string;
  file?: File;
}
const STEPS = [{
  id: 'details',
  label: 'Basic Details',
  icon: Building
}, {
  id: 'pricing',
  label: 'Pricing & Terms',
  icon: DollarSign
}, {
  id: 'amenities',
  label: 'Amenities',
  icon: CheckSquare
}, {
  id: 'photos',
  label: 'Photos & Docs',
  icon: Upload
}, {
  id: 'preview',
  label: 'Preview',
  icon: FileText
}];
const CITIES = ['Lagos', 'Abuja', 'Port Harcourt', 'Ibadan', 'Kano'];
const STATES = ['Lagos', 'FCT', 'Rivers', 'Oyo', 'Kano'];
const PROPERTY_TYPES: Property['propertyType'][] = ['apartment', 'house', 'condo', 'studio'];
const AMENITIES_LIST = ['Parking', 'Swimming Pool', 'Gym', '24/7 Security', 'Generator', 'Air Conditioning', 'Furnished', 'Pet Friendly', 'Balcony', 'Elevator', 'Garden', 'Laundry'];
const OWNERSHIP_DOC_TYPES = [{
  value: 'title_deed',
  label: 'Title Deed'
}, {
  value: 'certificate_of_occupancy',
  label: 'Certificate of Occupancy'
}, {
  value: 'purchase_agreement',
  label: 'Purchase Agreement'
}];
export function PropertyListingForm() {
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const [currentStep, setCurrentStep] = useState<ListingStep>('details');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<PropertyFormData>({
    address: '',
    city: '',
    state: '',
    propertyType: 'apartment',
    bedrooms: 1,
    bathrooms: 1,
    description: '',
    annualRent: 0,
    securityDeposit: 0,
    leaseDuration: 12,
    availableFrom: '',
    amenities: [],
    additionalFeatures: '',
    photos: [],
    ownershipDocs: []
  });
  const [newDocType, setNewDocType] = useState<OwnershipDoc['type']>('title_deed');
  const steps = STEPS;
  const currentStepIndex = steps.findIndex(s => s.id === currentStep);
  // Validation functions
  const validateStep = (step: ListingStep): {
    valid: boolean;
    errors: string[];
  } => {
    const errors: string[] = [];
    switch (step) {
      case 'details':
        if (!formData.address.trim()) errors.push('Property address is required');
        if (!formData.city) errors.push('City is required');
        if (!formData.state) errors.push('State is required');
        if (!formData.propertyType) errors.push('Property type is required');
        if (formData.bedrooms < 1) errors.push('At least 1 bedroom required');
        if (formData.bathrooms < 1) errors.push('At least 1 bathroom required');
        break;
      case 'pricing':
        if (formData.annualRent <= 0) errors.push('Annual rent must be greater than 0');
        if (formData.securityDeposit <= 0) errors.push('Security deposit is required');
        if (!formData.leaseDuration) errors.push('Lease duration is required');
        if (!formData.availableFrom) errors.push('Available date is required');
        break;
      case 'amenities':
        // Optional but show warning
        break;
      case 'photos':
        if (formData.photos.length === 0) errors.push('At least 1 photo is required');
        if (formData.ownershipDocs.length === 0) errors.push('At least 1 ownership document is required');
        break;
      case 'preview':
        break;
    }
    return {
      valid: errors.length === 0,
      errors
    };
  };
  const canProceed = (): boolean => {
    return validateStep(currentStep).valid;
  };
  const handleNext = () => {
    const validation = validateStep(currentStep);
    if (!validation.valid) {
      validation.errors.forEach(error => toast.error(error));
      return;
    }
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setCurrentStep(steps[nextIndex].id as ListingStep);
    }
  };
  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex].id as ListingStep);
    }
  };
  const goToStep = (step: ListingStep) => {
    setCurrentStep(step);
  };
  // Photo handling
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    if (formData.photos.length + files.length > 10) {
      toast.error('Maximum 10 photos allowed');
      return;
    }
    Array.from(files).forEach(file => {
      const id = Math.random().toString(36).substring(2, 9);
      const url = URL.createObjectURL(file);
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, {
          id,
          name: file.name,
          url
        }]
      }));
    });
    toast.success('Photos uploaded successfully');
  };
  const removePhoto = (id: string) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter(p => p.id !== id)
    }));
  };
  // Document handling
  const handleDocUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const id = Math.random().toString(36).substring(2, 9);
    setFormData(prev => ({
      ...prev,
      ownershipDocs: [...prev.ownershipDocs, {
        id,
        type: newDocType,
        name: file.name,
        file
      }]
    }));
    toast.success('Document uploaded successfully');
  };
  const removeDoc = (id: string) => {
    setFormData(prev => ({
      ...prev,
      ownershipDocs: prev.ownershipDocs.filter(d => d.id !== id)
    }));
  };
  // Amenity toggle
  const toggleAmenity = (amenity: string) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity) ? prev.amenities.filter(a => a !== amenity) : [...prev.amenities, amenity]
    }));
  };
  // Submit handlers
  const handleSaveAsDraft = async () => {
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    toast.success('Property saved as draft');
    setIsSubmitting(false);
    navigate('/landlord/properties');
  };
  const handleSubmitForVerification = async () => {
    const photosValidation = formData.photos.length > 0;
    const docsValidation = formData.ownershipDocs.length > 0;
    if (!photosValidation || !docsValidation) {
      if (!photosValidation) toast.error('At least 1 photo is required');
      if (!docsValidation) toast.error('At least 1 ownership document is required');
      return;
    }
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    toast.success('Property submitted for verification! Our team will review within 24 hours.');
    setIsSubmitting(false);
    navigate('/landlord/properties');
  };
  const monthlyEquivalent = formData.annualRent > 0 ? formData.annualRent / 12 : 0;
  return <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900 pb-16 lg:pb-0" data-id="element-3854">
      <Sidebar data-id="element-3855" />

      <main className="flex-1 lg:ml-[240px]" data-id="element-3856">
        <DashboardHeader data-id="element-3857" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-3858">
          {/* Header */}
          <div className="mb-8" data-id="element-3859">
            <div className="flex items-center space-x-3 mb-4" data-id="element-3860">
              <div className="h-12 w-12 bg-green-100 dark:bg-green-900 rounded-xl flex items-center justify-center" data-id="element-3861">
                <Building className="h-6 w-6 text-green-600 dark:text-green-400" data-id="element-3862" />
              </div>
              <div data-id="element-3863">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-3864">
                  List New Property
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3865">
                  Add your property to start receiving tenant applications
                </p>
              </div>
            </div>

            {/* Info Banner */}
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4" data-id="element-3866">
              <div className="flex items-start space-x-3" data-id="element-3867">
                <Info className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" data-id="element-3868" />
                <div className="flex-1" data-id="element-3869">
                  <h3 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-1" data-id="element-3870">
                    Property Verification
                  </h3>
                  <p className="text-xs text-green-800 dark:text-green-200 leading-relaxed" data-id="element-3871">
                    After submission, our team will verify your property within
                    24 hours. Verified properties get 3x more tenant
                    applications.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="mb-8" data-id="element-3872">
            <div className="flex items-center justify-between" data-id="element-3873">
              {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = step.id === currentStep;
              const isCompleted = index < currentStepIndex;
              return <Fragment key={step.id} data-id="element-3874">
                    <div className="flex flex-col items-center flex-1" data-id="element-3875">
                      <motion.div initial={false} animate={{
                    scale: isActive ? 1.1 : 1,
                    backgroundColor: isCompleted ? '#10b981' : isActive ? '#22c55e' : '#e5e7eb'
                  }} className={`h-12 w-12 rounded-full flex items-center justify-center mb-2 cursor-pointer transition-shadow ${isActive ? 'shadow-lg shadow-green-200 dark:shadow-green-900/50' : ''}`} onClick={() => {
                    if (isCompleted) goToStep(step.id as ListingStep);
                  }} data-id="element-3876">
                        {isCompleted ? <CheckCircle className="h-6 w-6 text-white" data-id="element-3877" /> : <StepIcon className={`h-6 w-6 ${isActive ? 'text-white' : 'text-gray-400 dark:text-gray-500'}`} data-id="element-3878" />}
                      </motion.div>
                      <p className={`text-xs font-medium text-center hidden sm:block ${isActive ? 'text-green-600 dark:text-green-400' : isCompleted ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-3879">
                        {step.label}
                      </p>
                    </div>
                    {index < steps.length - 1 && <div className={`h-0.5 flex-1 mx-2 mt-[-20px] ${index < currentStepIndex ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'}`} data-id="element-3880" />}
                  </Fragment>;
            })}
            </div>
          </div>

          {/* Step Content */}
          <AnimatePresence mode="wait" data-id="element-3881">
            <motion.div key={currentStep} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6" data-id="element-3882">
              {/* Step 1: Basic Details */}
              {currentStep === 'details' && <div className="space-y-6" data-id="element-3883">
                  <div data-id="element-3884">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2" data-id="element-3885">
                      Basic Details
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3886">
                      Tell us about your property
                    </p>
                  </div>

                  <div data-id="element-3887">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3888">
                      Property Address *
                    </label>
                    <div className="relative" data-id="element-3889">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-3890" />
                      <input type="text" value={formData.address} onChange={e => setFormData({
                    ...formData,
                    address: e.target.value
                  })} placeholder="e.g., 12 Admiralty Way, Lekki Phase 1" className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white" data-id="element-3891" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3892">
                    <div data-id="element-3893">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3894">
                        City *
                      </label>
                      <select value={formData.city} onChange={e => setFormData({
                    ...formData,
                    city: e.target.value
                  })} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white" data-id="element-3895">
                        <option value="" data-id="element-3896">Select City</option>
                        {CITIES.map(city => <option key={city} value={city} data-id="element-3897">
                            {city}
                          </option>)}
                      </select>
                    </div>

                    <div data-id="element-3898">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3899">
                        State *
                      </label>
                      <select value={formData.state} onChange={e => setFormData({
                    ...formData,
                    state: e.target.value
                  })} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white" data-id="element-3900">
                        <option value="" data-id="element-3901">Select State</option>
                        {STATES.map(state => <option key={state} value={state} data-id="element-3902">
                            {state}
                          </option>)}
                      </select>
                    </div>
                  </div>

                  <div data-id="element-3903">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3904">
                      Property Type *
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-3905">
                      {PROPERTY_TYPES.map(type => <button key={type} type="button" onClick={() => setFormData({
                    ...formData,
                    propertyType: type
                  })} className={`p-3 border rounded-lg text-sm font-medium capitalize transition-all ${formData.propertyType === type ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 ring-1 ring-green-500' : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400'}`} data-id="element-3906">
                          {type}
                        </button>)}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4" data-id="element-3907">
                    <div data-id="element-3908">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3909">
                        Bedrooms *
                      </label>
                      <input type="number" value={formData.bedrooms} onChange={e => setFormData({
                    ...formData,
                    bedrooms: Math.max(1, Math.min(10, parseInt(e.target.value) || 1))
                  })} min={1} max={10} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white" data-id="element-3910" />
                    </div>

                    <div data-id="element-3911">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3912">
                        Bathrooms *
                      </label>
                      <input type="number" value={formData.bathrooms} onChange={e => setFormData({
                    ...formData,
                    bathrooms: Math.max(1, Math.min(10, parseInt(e.target.value) || 1))
                  })} min={1} max={10} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white" data-id="element-3913" />
                    </div>
                  </div>

                  <div data-id="element-3914">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3915">
                      Description (Optional)
                    </label>
                    <textarea value={formData.description} onChange={e => setFormData({
                  ...formData,
                  description: e.target.value.slice(0, 500)
                })} placeholder="Describe your property features, neighborhood, etc." rows={4} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white" data-id="element-3916" />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right" data-id="element-3917">
                      {formData.description.length}/500 characters
                    </p>
                  </div>
                </div>}

              {/* Step 2: Pricing & Terms */}
              {currentStep === 'pricing' && <div className="space-y-6" data-id="element-3918">
                  <div data-id="element-3919">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2" data-id="element-3920">
                      Pricing & Terms
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3921">
                      Set your rent and lease terms
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-3922">
                    <div data-id="element-3923">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3924">
                        Annual Rent *
                      </label>
                      <div className="relative" data-id="element-3925">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium" data-id="element-3926">
                          ₦
                        </span>
                        <input type="number" value={formData.annualRent || ''} onChange={e => setFormData({
                      ...formData,
                      annualRent: parseInt(e.target.value) || 0
                    })} placeholder="1,800,000" className="w-full pl-8 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white" data-id="element-3927" />
                      </div>
                    </div>

                    <div data-id="element-3928">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3929">
                        Monthly Equivalent
                      </label>
                      <div className="px-4 py-3 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300" data-id="element-3930">
                        {monthlyEquivalent > 0 ? formatCurrency(monthlyEquivalent) : '₦0'}{' '}
                        / month
                      </div>
                    </div>
                  </div>

                  <div data-id="element-3931">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3932">
                      Security Deposit *
                    </label>
                    <div className="relative" data-id="element-3933">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium" data-id="element-3934">
                        ₦
                      </span>
                      <input type="number" value={formData.securityDeposit || ''} onChange={e => setFormData({
                    ...formData,
                    securityDeposit: parseInt(e.target.value) || 0
                  })} placeholder="150,000" className="w-full pl-8 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white" data-id="element-3935" />
                    </div>
                  </div>

                  <div data-id="element-3936">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3937">
                      Lease Duration *
                    </label>
                    <div className="grid grid-cols-3 gap-3" data-id="element-3938">
                      {([6, 12, 24] as const).map(duration => <button key={duration} type="button" onClick={() => setFormData({
                    ...formData,
                    leaseDuration: duration
                  })} className={`p-4 border rounded-lg text-center transition-all ${formData.leaseDuration === duration ? 'border-green-500 bg-green-50 dark:bg-green-900/20 ring-1 ring-green-500' : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'}`} data-id="element-3939">
                          <span className={`text-lg font-bold ${formData.leaseDuration === duration ? 'text-green-700 dark:text-green-400' : 'text-gray-900 dark:text-white'}`} data-id="element-3940">
                            {duration}
                          </span>
                          <span className={`block text-xs ${formData.leaseDuration === duration ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`} data-id="element-3941">
                            months
                          </span>
                        </button>)}
                    </div>
                  </div>

                  <div data-id="element-3942">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3943">
                      Available From *
                    </label>
                    <div className="relative" data-id="element-3944">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-3945" />
                      <input type="date" value={formData.availableFrom} onChange={e => setFormData({
                    ...formData,
                    availableFrom: e.target.value
                  })} min={new Date().toISOString().split('T')[0]} className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white" data-id="element-3946" />
                    </div>
                  </div>

                  {/* Expected Income Card */}
                  {formData.annualRent > 0 && <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-6 border border-green-200 dark:border-green-800" data-id="element-3947">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center" data-id="element-3948">
                        <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" data-id="element-3949" />
                        Expected Annual Income
                      </h3>
                      <div className="grid grid-cols-2 gap-4" data-id="element-3950">
                        <div data-id="element-3951">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1" data-id="element-3952">
                            Annual Rent
                          </p>
                          <p className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-3953">
                            {formatCurrency(formData.annualRent)}
                          </p>
                        </div>
                        <div data-id="element-3954">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1" data-id="element-3955">
                            You Receive (Upfront)
                          </p>
                          <p className="text-xl font-bold text-green-600 dark:text-green-400" data-id="element-3956">
                            {formatCurrency(formData.annualRent)}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-4" data-id="element-3957">
                        Renta pays you the full annual rent upfront. Tenants pay
                        monthly to Renta.
                      </p>
                    </div>}
                </div>}

              {/* Step 3: Amenities */}
              {currentStep === 'amenities' && <div className="space-y-6" data-id="element-3958">
                  <div data-id="element-3959">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2" data-id="element-3960">
                      Amenities & Features
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3961">
                      Select the amenities available at your property
                    </p>
                  </div>

                  {formData.amenities.length === 0 && <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4" data-id="element-3962">
                      <div className="flex items-start space-x-3" data-id="element-3963">
                        <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" data-id="element-3964" />
                        <p className="text-sm text-yellow-800 dark:text-yellow-200" data-id="element-3965">
                          Properties with amenities get 2x more applications.
                          Select at least one amenity.
                        </p>
                      </div>
                    </div>}

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-id="element-3966">
                    {AMENITIES_LIST.map(amenity => {
                  const isSelected = formData.amenities.includes(amenity);
                  return <button key={amenity} type="button" onClick={() => toggleAmenity(amenity)} className={`p-4 border rounded-lg text-left transition-all ${isSelected ? 'border-green-500 bg-green-50 dark:bg-green-900/20 ring-1 ring-green-500' : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'}`} data-id="element-3967">
                          <div className="flex items-center space-x-3" data-id="element-3968">
                            <div className={`h-5 w-5 rounded border-2 flex items-center justify-center ${isSelected ? 'border-green-500 bg-green-500' : 'border-gray-300 dark:border-gray-500'}`} data-id="element-3969">
                              {isSelected && <CheckCircle className="h-3 w-3 text-white" data-id="element-3970" />}
                            </div>
                            <span className={`text-sm font-medium ${isSelected ? 'text-green-700 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}`} data-id="element-3971">
                              {amenity}
                            </span>
                          </div>
                        </button>;
                })}
                  </div>

                  <div data-id="element-3972">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3973">
                      Additional Features (Optional)
                    </label>
                    <textarea value={formData.additionalFeatures} onChange={e => setFormData({
                  ...formData,
                  additionalFeatures: e.target.value
                })} placeholder="Any other features not listed above..." rows={3} className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white" data-id="element-3974" />
                  </div>
                </div>}

              {/* Step 4: Photos & Documents */}
              {currentStep === 'photos' && <div className="space-y-6" data-id="element-3975">
                  <div data-id="element-3976">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2" data-id="element-3977">
                      Photos & Documents
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3978">
                      Upload property photos and ownership documents
                    </p>
                  </div>

                  {/* Photos Section */}
                  <div data-id="element-3979">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3980">
                      Property Photos * ({formData.photos.length}/10)
                    </label>

                    {formData.photos.length > 0 && <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-id="element-3981">
                        {formData.photos.map(photo => <div key={photo.id} className="relative group" data-id="element-3982">
                            <img src={photo.url} alt={photo.name} className="w-full h-24 object-cover rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-3983" />
                            <button onClick={() => removePhoto(photo.id)} className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-3984">
                              <X className="h-3 w-3" data-id="element-3985" />
                            </button>
                          </div>)}
                      </div>}

                    {formData.photos.length < 10 && <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-green-500 dark:hover:border-green-400 transition-colors" data-id="element-3986">
                        <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" id="photo-upload" data-id="element-3987" />
                        <label htmlFor="photo-upload" className="cursor-pointer" data-id="element-3988">
                          <Image className="h-10 w-10 text-gray-400 mx-auto mb-3" data-id="element-3989" />
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1" data-id="element-3990">
                            Click to upload or drag and drop
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500" data-id="element-3991">
                            PNG, JPG up to 10MB (Max 10 images)
                          </p>
                        </label>
                      </div>}
                  </div>

                  {/* Documents Section */}
                  <div data-id="element-3992">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-3993">
                      Ownership Documents *
                    </label>

                    {formData.ownershipDocs.length > 0 && <div className="space-y-2 mb-4" data-id="element-3994">
                        {formData.ownershipDocs.map(doc => <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600" data-id="element-3995">
                            <div className="flex items-center space-x-3" data-id="element-3996">
                              <FileText className="h-5 w-5 text-green-600 dark:text-green-400" data-id="element-3997" />
                              <div data-id="element-3998">
                                <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-3999">
                                  {doc.name}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize" data-id="element-4000">
                                  {doc.type.replace(/_/g, ' ')}
                                </p>
                              </div>
                            </div>
                            <button onClick={() => removeDoc(doc.id)} className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded" data-id="element-4001">
                              <Trash2 className="h-4 w-4" data-id="element-4002" />
                            </button>
                          </div>)}
                      </div>}

                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4" data-id="element-4003">
                      <div className="flex items-center space-x-3 mb-3" data-id="element-4004">
                        <select value={newDocType} onChange={e => setNewDocType(e.target.value as OwnershipDoc['type'])} className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white" data-id="element-4005">
                          {OWNERSHIP_DOC_TYPES.map(type => <option key={type.value} value={type.value} data-id="element-4006">
                              {type.label}
                            </option>)}
                        </select>
                      </div>

                      <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 text-center hover:border-green-500 dark:hover:border-green-400 transition-colors" data-id="element-4007">
                        <input type="file" accept="image/*,.pdf" onChange={handleDocUpload} className="hidden" id="doc-upload" data-id="element-4008" />
                        <label htmlFor="doc-upload" className="cursor-pointer" data-id="element-4009">
                          <Plus className="h-6 w-6 text-gray-400 mx-auto mb-2" data-id="element-4010" />
                          <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4011">
                            Upload ownership document
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500" data-id="element-4012">
                            PDF or images
                          </p>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>}

              {/* Step 5: Preview */}
              {currentStep === 'preview' && <div className="space-y-6" data-id="element-4013">
                  <div data-id="element-4014">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2" data-id="element-4015">
                      Review & Submit
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4016">
                      Review your property details before submitting
                    </p>
                  </div>

                  {/* Property Preview Card */}
                  <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden" data-id="element-4017">
                    {/* Photo Preview */}
                    {formData.photos.length > 0 && <div className="relative h-48 bg-gray-100 dark:bg-gray-700" data-id="element-4018">
                        <img src={formData.photos[0].url} alt="Property" className="w-full h-full object-cover" data-id="element-4019" />
                        <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded" data-id="element-4020">
                          {formData.photos.length} photos
                        </div>
                      </div>}

                    <div className="p-4 space-y-4" data-id="element-4021">
                      {/* Basic Details */}
                      <div className="flex items-start justify-between" data-id="element-4022">
                        <div data-id="element-4023">
                          <h3 className="font-bold text-gray-900 dark:text-white" data-id="element-4024">
                            {formData.address || 'Property Address'}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4025">
                            {formData.city}, {formData.state}
                          </p>
                        </div>
                        <button onClick={() => goToStep('details')} className="text-xs text-green-600 dark:text-green-400 hover:underline" data-id="element-4026">
                          Edit
                        </button>
                      </div>

                      <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400" data-id="element-4027">
                        <span className="capitalize" data-id="element-4028">
                          {formData.propertyType}
                        </span>
                        <span data-id="element-4029">•</span>
                        <span data-id="element-4030">{formData.bedrooms} bed</span>
                        <span data-id="element-4031">•</span>
                        <span data-id="element-4032">{formData.bathrooms} bath</span>
                      </div>

                      {/* Pricing */}
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-4033">
                        <div className="flex items-center justify-between" data-id="element-4034">
                          <div data-id="element-4035">
                            <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-4036">
                              {formatCurrency(formData.annualRent)}
                              <span className="text-sm font-normal text-gray-500 dark:text-gray-400" data-id="element-4037">
                                /year
                              </span>
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4038">
                              {formatCurrency(monthlyEquivalent)}/month •{' '}
                              {formData.leaseDuration} months lease
                            </p>
                          </div>
                          <button onClick={() => goToStep('pricing')} className="text-xs text-green-600 dark:text-green-400 hover:underline" data-id="element-4039">
                            Edit
                          </button>
                        </div>
                      </div>

                      {/* Amenities */}
                      {formData.amenities.length > 0 && <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-4040">
                          <div className="flex items-center justify-between mb-2" data-id="element-4041">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4042">
                              Amenities
                            </p>
                            <button onClick={() => goToStep('amenities')} className="text-xs text-green-600 dark:text-green-400 hover:underline" data-id="element-4043">
                              Edit
                            </button>
                          </div>
                          <div className="flex flex-wrap gap-2" data-id="element-4044">
                            {formData.amenities.map(amenity => <span key={amenity} className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded-full" data-id="element-4045">
                                {amenity}
                              </span>)}
                          </div>
                        </div>}

                      {/* Documents */}
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4" data-id="element-4046">
                        <div className="flex items-center justify-between mb-2" data-id="element-4047">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-4048">
                            Documents
                          </p>
                          <button onClick={() => goToStep('photos')} className="text-xs text-green-600 dark:text-green-400 hover:underline" data-id="element-4049">
                            Edit
                          </button>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400" data-id="element-4050">
                          <span className="flex items-center" data-id="element-4051">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-1" data-id="element-4052" />
                            {formData.photos.length} photos
                          </span>
                          <span className="flex items-center" data-id="element-4053">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-1" data-id="element-4054" />
                            {formData.ownershipDocs.length} documents
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Verification Notice */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4" data-id="element-4055">
                    <div className="flex items-start space-x-3" data-id="element-4056">
                      <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" data-id="element-4057" />
                      <div className="flex-1" data-id="element-4058">
                        <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1" data-id="element-4059">
                          What happens next?
                        </h4>
                        <ul className="text-xs text-blue-800 dark:text-blue-200 space-y-1" data-id="element-4060">
                          <li data-id="element-4061">
                            • Our team will verify your ownership documents
                            within 24 hours
                          </li>
                          <li data-id="element-4062">
                            • Once verified, your property will be visible to
                            tenants
                          </li>
                          <li data-id="element-4063">
                            • You'll receive notifications for tenant
                            applications
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>}
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="mt-8 flex justify-between" data-id="element-4064">
            <button onClick={handleBack} disabled={currentStepIndex === 0} className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center" data-id="element-4065">
              <ChevronLeft className="h-5 w-5 mr-1" data-id="element-4066" />
              Back
            </button>

            {currentStep === 'preview' ? <div className="flex space-x-3" data-id="element-4067">
                <button onClick={handleSaveAsDraft} disabled={isSubmitting} className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors" data-id="element-4068">
                  Save as Draft
                </button>
                <button onClick={handleSubmitForVerification} disabled={isSubmitting} className="px-8 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center shadow-lg shadow-green-200 dark:shadow-none" data-id="element-4069">
                  {isSubmitting ? <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" data-id="element-4070"></div>
                      Submitting...
                    </> : <>
                      Submit for Verification
                      <ChevronRight className="ml-2 h-5 w-5" data-id="element-4071" />
                    </>}
                </button>
              </div> : <button onClick={handleNext} disabled={!canProceed()} className="px-8 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center shadow-lg shadow-green-200 dark:shadow-none" data-id="element-4072">
                Next
                <ChevronRight className="ml-2 h-5 w-5" data-id="element-4073" />
              </button>}
          </div>
        </div>
      </main>
    </div>;
}